import React, { useState } from 'react'
import {
  Button,
  Flex,
  H2,
  Input,
  Line,
  SelectDropdown,
  Span,
  ToolTipBlack,
} from '../../lib/components'
import { orderFilters, orderStatusFilters, ordersColumnDescription } from '../../lib/utils/message'
import { OrderCard } from './order-card'
import { useBreakpoint } from '../../lib/utils/hooks'
import { SM } from '../../lib/utils/constants'
import { Pagination } from '../../lib/components/Pagination'
import './order.scss'
import { Order, OrdersPresenterProps } from '../../lib/types'

const OrdersPresenter = ({
  pagination,
  handleCurrentPage,
  currentPage,
  orders,
  resetSearchParams,
  searchParams,
  handleSearchParams,
  handleOrderClick,
}: OrdersPresenterProps) => {
  const { breakpoint } = useBreakpoint()
  const [columnOrder, setColumnOrder] = useState([
    'order_id',
    'order_type',
    'order_status',
    'opportunity_title',
    'order_creation_date',
    'unit_allocation_date',
    'investor_portfolio_id',
    'investor_name',
    'investor_email',
    'kyc',
    'welcome_letter',
    'client_agreement',
    'order_amount',
    'total_amount_to_be_transferred',
    'payment_copy',
    'order_completed',
    'order_details',
  ])
  const [draggedColumn, setDraggedColumn] = useState<string | null>(null)

  const handleDragStart = (e: React.DragEvent, columnId: string) => {
    setDraggedColumn(columnId)
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  const handleDrop = (e: React.DragEvent, targetColumnId: string) => {
    e.preventDefault()
    if (!draggedColumn || draggedColumn === targetColumnId) return

    const newColumnOrder = [...columnOrder]
    const draggedIndex = newColumnOrder.indexOf(draggedColumn)
    const targetIndex = newColumnOrder.indexOf(targetColumnId)

    newColumnOrder.splice(draggedIndex, 1)
    newColumnOrder.splice(targetIndex, 0, draggedColumn)

    setColumnOrder(newColumnOrder)
    setDraggedColumn(null)
  }

  const getColumnHeader = (columnId: string) => {
    const headers: Record<string, string | JSX.Element> = {
      order_id: 'Order ID',
      order_type: 'Order Type',
      order_status: 'Order Status',
      opportunity_title: 'Opportunity Name',
      order_creation_date: (
        <div className='table-header-with-icon-content'>
          Order Creation Date
          <ToolTipBlack variant='info-icon' classes={['ml-8']}>
            {ordersColumnDescription?.order_creation_date}
          </ToolTipBlack>
        </div>
      ),
      unit_allocation_date: (
        <div className='table-header-with-icon-content'>
          Allocation Date
          <ToolTipBlack variant='info-icon' classes={['ml-8']}>
            {ordersColumnDescription?.allocation_date}
          </ToolTipBlack>
        </div>
      ),
      investor_portfolio_id: 'Client Portfolio ID',
      investor_name: 'Client Name',
      investor_email: 'Client Email',
      kyc: 'KYC',
      welcome_letter: 'Welcome Letter',
      client_agreement: 'Client Agreement',
      order_amount: 'Order Amount',
      total_amount_to_be_transferred: 'Total amount to be transferred',
      payment_copy: 'SWIFT Copy / Transaction Ref. Number',
      order_completed: 'Order Completed',
      order_details: 'Order Details',
    }
    return headers[columnId]
  }

  return (
    <Flex variant='column-center'>
      <H2 classes={['mb-48']} text='Orders' />
      <div className='toolbar-layout gap-12 mb-16 mt-16'>
        <Input
          name='keyword'
          value={searchParams.keyword}
          handleChange={(e) => handleSearchParams({ name: e.target.name, value: e.target.value })}
          placeholder='Search'
        />
        <SelectDropdown
          name='filterBy'
          id='filterId'
          formId=''
          list={orderFilters}
          value={searchParams.filterBy}
          placeholder={'Filter by Type'}
          handleChange={(val) => handleSearchParams({ name: 'filterBy', value: val })}
        />
        <SelectDropdown
          name='orderStatus'
          id='orderStatusId'
          formId=''
          list={orderStatusFilters}
          value={searchParams.orderStatus}
          placeholder={'Filter by Status'}
          handleChange={(val) => handleSearchParams({ name: 'orderStatus', value: val })}
        />
        <div className='sort-reset'>
          <Button
            handleClick={() => resetSearchParams()}
            classes={['reset-btn']}
            variant='secondary-btn'
            text='Reset'
          />
        </div>
      </div>
      <Line />
      {orders && orders.length > 0 ? (
        <>
          <div className='w-100'>
            <Flex
              variant='row'
              classes={['mb-24', 'table_scroll_horizontal']}
              dataTestId='orders-table-container'
            >
              <table className={'styled-table'}>
                <thead>
                  <tr>
                    {columnOrder.map((columnId) => (
                      <th
                        key={columnId}
                        draggable
                        onDragStart={(e) => handleDragStart(e, columnId)}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, columnId)}
                        className={
                          columnId === 'order_creation_date' || columnId === 'unit_allocation_date'
                            ? 'table-header-with-icon draggable-header'
                            : 'draggable-header'
                        }
                        style={{ cursor: 'move' }}
                      >
                        {getColumnHeader(columnId)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {orders &&
                    orders.length > 0 &&
                    orders.map(
                      (
                        {
                          order_id,
                          opportunity_title,
                          order_creation_date,
                          investor_portfolio_id,
                          investor_name,
                          investor_email,
                          order_amount,
                          total_amount_to_be_transferred,
                          investment_completed,
                          opportunity_slug,
                          opportunity_id,
                          investor_id,
                          order_details_id,
                          completed_steps,
                          unit_allocation_date,
                          investor_account_status,
                          welcome_letter_link,
                          agreement_document_id,
                          investor_kyc_id,
                          payment_copy,
                          transaction_reference_number,
                          order_status,
                          order_type,
                          withdrawal_completed,
                        }: Order,
                        idx: number,
                      ) => (
                        <OrderCard
                          key={order_id + idx}
                          order_id={order_id}
                          order_type={order_type}
                          order_status={order_status}
                          opportunity_title={opportunity_title}
                          order_creation_date={order_creation_date}
                          unit_allocation_date={unit_allocation_date}
                          investor_portfolio_id={investor_portfolio_id}
                          investor_name={investor_name}
                          investor_email={investor_email}
                          order_amount={order_amount}
                          total_amount_to_be_transferred={total_amount_to_be_transferred}
                          withdrawal_completed={withdrawal_completed}
                          investment_completed={investment_completed}
                          completed_steps={completed_steps}
                          investor_account_status={investor_account_status}
                          welcome_letter_link={welcome_letter_link}
                          agreement_document_id={agreement_document_id}
                          investor_kyc_id={investor_kyc_id}
                          payment_copy={payment_copy}
                          transaction_reference_number={transaction_reference_number}
                          columnOrder={columnOrder}
                          handleOrderClick={() =>
                            handleOrderClick(
                              opportunity_slug,
                              opportunity_id,
                              investor_id,
                              order_details_id,
                              order_type,
                            )
                          }
                        />
                      ),
                    )}
                </tbody>
              </table>
            </Flex>
          </div>
          {breakpoint && breakpoint === SM ? null : (
            <Pagination
              currentPage={currentPage}
              handleCurrentPage={handleCurrentPage}
              pagination={pagination}
            />
          )}
        </>
      ) : (
        <Flex variant={'column-center'} classes={['mt-12']}>
          <Span classes={['center']} variant={'thin'} text='No Orders Found' />
        </Flex>
      )}
    </Flex>
  )
}

export { OrdersPresenter }
